import Phaser from "phaser";
import { GameConfig } from "@/config/GameConfig";
import { EntryScene } from "@/scenes/EntryScene";
import { MenuScene } from "@/scenes/MenuScene";
import { WorldSelectScene } from "@/scenes/WorldSelectScene";
import { LevelSelectScene } from "@/scenes/LevelSelectScene";
import { GameScene } from "@/scenes/GameScene";
import { LeaderboardScene } from "@/scenes/LeaderboardScene";
import { DailyChallengeScene } from "@/scenes/DailyChallengeScene";
import { DailyResultScene } from "@/scenes/DailyResultScene";
import { DailyLeaderboardScene } from "@/scenes/DailyLeaderboardScene";
import { ScoreChallengeScene } from "@/scenes/ScoreChallengeScene";
import { EndlessResultScene } from "@/scenes/EndlessResultScene";
import { EndlessLeaderboardScene } from "@/scenes/EndlessLeaderboardScene";
import { bootstrapOnlineFeatures } from "@/online/bootstrap";
import { initAnalytics } from "@/analytics/posthogClient";
import { initErrorReporting } from "@/analytics/errorReporting";
import { OnboardingStorage } from "@/progress/OnboardingStorage";
import { applyHighDprCanvas, applyHighDprTextDefault } from "@/rendering/highDpr";

// Safe no-op outside production on the exact production hostname - see
// src/analytics/shouldEnableAnalytics.ts. Never blocks/affects Phaser
// starting below either way.
initAnalytics();

// PR28: the app's only global uncaught-error/unhandled-rejection
// listeners - must be installed before anything else below has a chance
// to throw/reject, so no early failure goes unreported. A safe no-op
// wherever analytics itself is disabled (see `initErrorReporting()`'s own
// doc) and idempotent, so it's safe this early regardless of load order.
initErrorReporting();

// Instant Play & Onboarding V1: must run before the very first scene's
// `create()` (whichever one that is - `EntryScene` on a normal visit, or
// a deep-linked `ScoreChallengeScene`/`AudioLabScene`), so a legacy
// player's evidence (`TutorialStorage.hasSeenIntro()`) is always read
// before anything in THIS session could change it - see
// `OnboardingStorage.ensureInitialized()`'s own doc for why that ordering
// matters. Synchronous and cheap (a single localStorage read, and a write
// only the very first time ever) - never blocks/delays Phaser starting.
OnboardingStorage.ensureInitialized();

// PR31 (rendering sharpness): capped at 2, never the raw, uncapped DPR
// some phones report (3-4x) - see `src/rendering/highDpr.ts`'s own doc for
// the full "why" (Phaser 3.60+ removed the old `resolution` game-config
// option entirely; this reimplements it by hand). This game renders
// everything through `Graphics`/`Text` every frame (no textures/sprites
// anywhere in the codebase), so a higher value is a direct, ongoing
// per-frame fill-rate/text-re-rasterization cost, not a one-time texture-
// memory cost - DPR 2 already captures the large majority of the
// perceptible sharpness gain over DPR 1, while the raw 3-4x some phones
// report would quadratically multiply the pixel count rasterized every
// frame for comparatively little extra visible sharpness - a real risk on
// mid/low-end Android GPUs given this game's tween-heavy CHAOS/WORMHOLE/
// RUSH feedback effects. `|| 1` guards environments where
// `devicePixelRatio` is unset. Must be verified on real high-DPI hardware
// (sharpness AND frame rate) - see PR31's manual-QA checklist; this
// project's Vitest suite cannot meaningfully judge either.
const dpr = Math.min(window.devicePixelRatio || 1, 2);

// Must run before the very first Scene's `create()` calls `this.add.text()`
// - see `applyHighDprTextDefault()`'s own doc.
applyHighDprTextDefault(dpr);

const game = new Phaser.Game({
  type: Phaser.AUTO,
  parent: "app",
  backgroundColor: GameConfig.colors.background,
  scale: {
    mode: Phaser.Scale.RESIZE,
    width: window.innerWidth,
    height: window.innerHeight,
  },
  scene: [
    EntryScene,
    MenuScene,
    WorldSelectScene,
    LevelSelectScene,
    GameScene,
    LeaderboardScene,
    DailyChallengeScene,
    DailyResultScene,
    DailyLeaderboardScene,
    ScoreChallengeScene,
    EndlessResultScene,
    EndlessLeaderboardScene,
    // `AudioLabScene` (the `?qa=audio` QA-only sound lab) is deliberately
    // NOT registered here - it and its entire Round 1/Round 2/Final
    // Palette Validation candidate tree are lazy-loaded only after the QA
    // host/route guard passes (see `EntryScene`'s `consumeAudioLabRoute`
    // branch), so a normal production visitor's bundle never has to
    // download unselected-candidate audio code it will never run.
  ],
  render: {
    antialias: true,
    pixelArt: false,
  },
});

applyHighDprCanvas(game, dpr);

// Fire-and-forget: never blocks Phaser from starting/rendering the first
// frame. A slow or failed connection just means the game starts in
// offline mode - see AuthService/OnlineFeaturesStorage.
bootstrapOnlineFeatures();
